type Cookie = {
  domain?: string;
  name: string;
  maxAge?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  value?: string;
};

function getBaseDomains(hostname: string, limit: number = 3): string[] {
  const parts: string[] = hostname.split('.');
  const domains: string[] = [];

  for (let i: number = 0; i < Math.min(parts.length - 1, limit); i += 1) {
    domains.push(parts.slice(i).join('.'));
  }

  return domains;
}

export function getCookies() {
  const currentDomain = document.domain;

  const cookies: Cookie[] = [];
  const regex = /^\s*([^=]+)\s*=\s*(.*?)$/;

  /* Helper to delete Concord cookies */
  function deleteConcordCookie({
    name,
    domain = '.concord.tech',
    path = '/',
  }: Cookie) {
    document.cookie = `${name}=; Path=${path}; Domain=${domain}; Max-Age=-99999999;`;
  }

  /* Delete Concord domain cookies if we're not on Concord domain
  This is to prevent Concord cookies from being captured for a client's domain
  */
  if (!currentDomain.endsWith('concord.tech')) {
    const cookieStrings = document.cookie.split(';');
    cookieStrings.forEach((cookie) => {
      const match = regex.exec(cookie);
      if (match !== null) {
        const name = match[1];
        deleteConcordCookie({
          name,
        });
      }
    });
  }

  const remainingCookies = document.cookie.split(';');

  remainingCookies.forEach((cookie) => {
    const match = regex.exec(cookie);
    if (match !== null) {
      const cookieName = match[1];
      const cookieValue = match[2];
      if (!cookieName.startsWith('concord-')) {
        cookies.push({
          name: cookieName,
          value: cookieValue,
        });
      }
    }
  });
  return cookies;
}

export function deleteCookie({ name, domain, path = '/' }: Cookie) {
  const deleteCookieString = (cookieStr: string) => {
    document.cookie = cookieStr;
  };

  let str = `${name}=; Max-Age=-99999999;`;
  deleteCookieString(str); // without path and domain

  str += ` path=${path};`;
  deleteCookieString(str); // with path

  const deleteWithDomain = (domain: string) => {
    deleteCookieString(`${str} domain=${domain};`); // with path and domain
    if (!domain.startsWith('.')) {
      deleteCookieString(`${str} domain=.${domain};`); // with path and domain with leading period
    }
  };

  if (domain) {
    deleteWithDomain(domain);
  }

  const { hostname } = window.location;
  const baseDomains = getBaseDomains(hostname);
  deleteWithDomain(hostname);

  baseDomains.forEach((baseDomain) => {
    deleteWithDomain(baseDomain);
  });
}

export function getCookie({ name }: Cookie) {
  const cookieStrings = document.cookie.split(';');
  const regex = /^\s*([^=]+)\s*=\s*(.*?)$/;

  const cookie = cookieStrings.find((cookieStr) => {
    const match = regex.exec(cookieStr.trim());
    return match !== null && match[1] === name;
  });

  const match = cookie ? regex.exec(cookie.trim()) : null;
  return match ? match[2] : null;
}

export function setCookie({
  name,
  value,
  maxAge,
  path = '/',
  domain,
  secure = true,
  sameSite = 'Strict',
}: Cookie) {
  let str = `${name}=${value};`;

  if (maxAge) {
    str += ` max-age=${maxAge};`;
  }

  if (path) {
    str += ` path=${path};`;
  }
  if (domain) {
    str += ` domain=${domain};`;
  }
  if (secure) {
    str += ' Secure;';
  }
  if (sameSite) {
    str += ` SameSite=${sameSite};`;
  }

  document.cookie = str;
}
